// diagramLayout — Pure layout-algoritmer för multi-sheet-diagram.
// Används av LayoutAgent (D9) och buildWorkspaceFromPrompt (Y2) för att
// producera läsbara default-layouts utan att förlita sig på dagre eller
// annan extern dep.
//
// Alla funktioner är side-effect-fria: input-arrayen ändras aldrig,
// ny array returneras. Grid-snap 20px appliceras på all output.
//
// Supported algoritmer:
// - gridLayout(blocks, opts)    — N×M-grid centrerad på origin
// - radialLayout(blocks, center) — nav + ekrar runt center-block
// - linearLayout(blocks, opts)   — en rad, vertical eller horizontal
//
// Plus detectOverlaps(blocks) som identifierar block-par som overlappar
// visuellt — används av LayoutAgent för iterative refactor.

import type { WorkflowBlock } from '@/types/workflow';
import {
  DEFAULT_BLOCK_WIDTH,
  DEFAULT_BLOCK_HEIGHT,
  COLUMN_STRIDE,
  ROW_STRIDE,
  snapToGrid,
} from '@/lib/aiDiagramTools';

export type LayoutAlgorithm = 'grid' | 'radial' | 'linear-h' | 'linear-v';

export interface GridLayoutOptions {
  columns?: number;
  startX?: number;
  startY?: number;
  columnStride?: number;
  rowStride?: number;
}

/**
 * Lägg blocken i en N-kolumn-grid centrerad på opt:s startpunkt.
 * Blockens ordning i arrayen bestämmer ordningen i gridden (radvis,
 * kolumn för kolumn).
 */
export function gridLayout<T extends WorkflowBlock>(
  blocks: T[],
  opts: GridLayoutOptions = {},
): T[] {
  const columns = Math.max(1, opts.columns ?? 3);
  const startX = opts.startX ?? -1000;
  const startY = opts.startY ?? -400;
  const colStride = opts.columnStride ?? COLUMN_STRIDE;
  const rowStride = opts.rowStride ?? ROW_STRIDE;
  return blocks.map((b, i) => ({
    ...b,
    x: snapToGrid(startX + (i % columns) * colStride),
    y: snapToGrid(startY + Math.floor(i / columns) * rowStride),
  }));
}

export interface RadialLayoutOptions {
  /** Position för center-blocket (första blocket). Default (0,0). */
  centerX?: number;
  centerY?: number;
  /** Radie för ekrar-blocken. Default 260. */
  radius?: number;
  /** Start-vinkel i radianer. Default -PI/2 (top). */
  startAngle?: number;
}

/**
 * Placera första blocket i center, resten i en cirkel runt det. Bra för
 * hub-and-spoke-arkitekturer.
 */
export function radialLayout<T extends WorkflowBlock>(
  blocks: T[],
  opts: RadialLayoutOptions = {},
): T[] {
  if (blocks.length === 0) return [];
  const cx = opts.centerX ?? 0;
  const cy = opts.centerY ?? 0;
  const radius = opts.radius ?? 260;
  const startAngle = opts.startAngle ?? -Math.PI / 2;
  const spokes = blocks.length - 1;
  return blocks.map((b, i) => {
    if (i === 0) return { ...b, x: snapToGrid(cx), y: snapToGrid(cy) };
    const angle = startAngle + ((i - 1) / spokes) * 2 * Math.PI;
    return {
      ...b,
      x: snapToGrid(cx + Math.cos(angle) * radius),
      y: snapToGrid(cy + Math.sin(angle) * radius),
    };
  });
}

export interface LinearLayoutOptions {
  /** Start-X. Default -1000. */
  startX?: number;
  /** Start-Y. Default 0. */
  startY?: number;
  /** Stride mellan blocks. Default COLUMN_STRIDE (för horizontal) / ROW_STRIDE. */
  stride?: number;
}

/**
 * Placera blocken på en horisontell rad (vänster till höger). Bra för
 * pipelines + state-machines.
 */
export function linearLayoutHorizontal<T extends WorkflowBlock>(
  blocks: T[],
  opts: LinearLayoutOptions = {},
): T[] {
  const startX = opts.startX ?? -1000;
  const startY = opts.startY ?? 0;
  const stride = opts.stride ?? COLUMN_STRIDE;
  return blocks.map((b, i) => ({
    ...b,
    x: snapToGrid(startX + i * stride),
    y: snapToGrid(startY),
  }));
}

/**
 * Placera blocken i en vertikal kolumn (top till bottom).
 */
export function linearLayoutVertical<T extends WorkflowBlock>(
  blocks: T[],
  opts: LinearLayoutOptions = {},
): T[] {
  const startX = opts.startX ?? 0;
  const startY = opts.startY ?? -400;
  const stride = opts.stride ?? ROW_STRIDE;
  return blocks.map((b, i) => ({
    ...b,
    x: snapToGrid(startX),
    y: snapToGrid(startY + i * stride),
  }));
}

// ── Overlap-detection ─────────────────────────────────────────

export interface BlockBBox {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Bygg axis-aligned bounding-box för ett block (center-origin → box).
 */
export function blockBBox(
  b: Pick<WorkflowBlock, 'id' | 'x' | 'y'>,
  width: number = DEFAULT_BLOCK_WIDTH,
  height: number = DEFAULT_BLOCK_HEIGHT,
): BlockBBox {
  const halfW = width / 2;
  const halfH = height / 2;
  return {
    id: b.id,
    left: b.x - halfW,
    top: b.y - halfH,
    right: b.x + halfW,
    bottom: b.y + halfH,
  };
}

export interface OverlapPair {
  a: string;
  b: string;
  /** Area av överlappet i pixlar^2. */
  area: number;
}

/**
 * Returnera alla par av blocks som överlappar visuellt. O(N²) — OK för
 * sheets med <200 blocks. Filtrera self-comparisons.
 */
export function detectOverlaps(
  blocks: Array<Pick<WorkflowBlock, 'id' | 'x' | 'y'>>,
  width: number = DEFAULT_BLOCK_WIDTH,
  height: number = DEFAULT_BLOCK_HEIGHT,
): OverlapPair[] {
  const boxes = blocks.map((b) => blockBBox(b, width, height));
  const pairs: OverlapPair[] = [];
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i];
      const b = boxes[j];
      const overlapW = Math.min(a.right, b.right) - Math.max(a.left, b.left);
      const overlapH = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (overlapW > 0 && overlapH > 0) {
        pairs.push({ a: a.id, b: b.id, area: overlapW * overlapH });
      }
    }
  }
  return pairs;
}

/**
 * Refactor:a layout:en tills inga overlaps finns. Enkel approach: om något
 * overlappar, kör om gridLayout med ökande kolumn-stride tills clean (upp
 * till maxIterations). Returnerar alltid en ren layout.
 */
export function refactorLayoutNoOverlap<T extends WorkflowBlock>(
  blocks: T[],
  opts: { columns?: number; maxIterations?: number } = {},
): T[] {
  const cols = opts.columns ?? 3;
  const maxIter = opts.maxIterations ?? 5;
  let current = gridLayout(blocks, { columns: cols });
  for (let i = 0; i < maxIter; i++) {
    const overlaps = detectOverlaps(current);
    if (overlaps.length === 0) return current;
    current = gridLayout(blocks, {
      columns: cols,
      columnStride: COLUMN_STRIDE + (i + 1) * 40,
      rowStride: ROW_STRIDE + (i + 1) * 20,
    });
  }
  return current;
}

/**
 * Applicera en layout-algoritm baserat på sheet-count + block-count.
 * Enkel heuristik så LayoutAgent slipper välja explicit:
 * - 1-3 blocks → linear-horizontal
 * - 4-9 blocks → 3-col grid
 * - 10+ blocks → 4-col grid
 * - Override via `hint`.
 */
export function autoLayout<T extends WorkflowBlock>(
  blocks: T[],
  hint?: LayoutAlgorithm,
): T[] {
  const algo = hint ?? chooseAlgorithm(blocks.length);
  switch (algo) {
    case 'grid': return gridLayout(blocks, { columns: blocks.length >= 10 ? 4 : 3 });
    case 'radial': return radialLayout(blocks);
    case 'linear-h': return linearLayoutHorizontal(blocks);
    case 'linear-v': return linearLayoutVertical(blocks);
  }
}

function chooseAlgorithm(n: number): LayoutAlgorithm {
  if (n <= 3) return 'linear-h';
  if (n <= 9) return 'grid';
  return 'grid';
}
